import { useState, useEffect } from "react";
import { Poll } from "@/types/poll";
import { supabase } from "@/integrations/supabase/client";

export type VoteFilterType =
  | "all"
  | "verified"
  | "unverified"
  | "anonymous"
  | "anonymized";

interface VoteBreakdown {
  [optionId: string]: {
    verified: number;
    unverified: number;
    anonymous: number;
    anonymized: number;
    total: number;
  };
  _totals: {
    verified: number;
    unverified: number;
    anonymous: number;
    anonymized: number;
    total: number;
  };
}

interface VoteStats {
  totalVotes: number;
  verifiedVotes: number;
  unverifiedVotes: number;
  anonymousVotes: number;
  anonymizedVotes: number;
  verifiedPercentage: number;
  unverifiedPercentage: number;
  anonymousPercentage: number;
  anonymizedPercentage: number;
}

export const useVoteFiltering = (poll: Poll | null) => {
  const [voteFilter, setVoteFilter] = useState<VoteFilterType>("all");
  const [voteBreakdown, setVoteBreakdown] = useState<VoteBreakdown | null>(
    null
  );
  const [isLoadingBreakdown, setIsLoadingBreakdown] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch vote breakdown when poll changes
  useEffect(() => {
    const fetchVoteBreakdown = async () => {
      if (!poll?.id) {
        setVoteBreakdown(null);
        return;
      }

      setIsLoadingBreakdown(true);
      setError(null);

      try {
        const { data, error } = await supabase.rpc("get_poll_vote_breakdown", {
          poll_id_param: poll.id,
        });

        if (error) {
          console.error("Error fetching vote breakdown:", error);
          setError("Failed to load vote breakdown");
          return;
        }

        if (data && typeof data === "object") {
          setVoteBreakdown(data as VoteBreakdown);
        } else {
          setVoteBreakdown(null);
        }
      } catch (err) {
        console.error("Exception fetching vote breakdown:", err);
        setError("Failed to load vote breakdown");
      } finally {
        setIsLoadingBreakdown(false);
      }
    };

    fetchVoteBreakdown();
  }, [poll?.id]);

  // Calculate vote statistics with percentages
  const calculatePercentages = (votes: number, total: number) => {
    return total > 0 ? Math.round((votes / total) * 100) : 0;
  };

  const totalVotes = voteBreakdown?._totals?.total || poll?.total_votes || 0;
  const verifiedVotes = voteBreakdown?._totals?.verified || 0;
  const unverifiedVotes = voteBreakdown?._totals?.unverified || 0;
  const anonymousVotes =
    voteBreakdown?._totals?.anonymous || poll?.anonymous_votes || 0;
  const anonymizedVotes = voteBreakdown?._totals?.anonymized || 0;

  const voteStats: VoteStats = {
    totalVotes,
    verifiedVotes,
    unverifiedVotes,
    anonymousVotes,
    anonymizedVotes,
    verifiedPercentage: calculatePercentages(verifiedVotes, totalVotes),
    unverifiedPercentage: calculatePercentages(unverifiedVotes, totalVotes),
    anonymousPercentage: calculatePercentages(anonymousVotes, totalVotes),
    anonymizedPercentage: calculatePercentages(anonymizedVotes, totalVotes),
  };

  // Filter poll options based on selected vote type
  const filteredOptions =
    poll?.questions?.options?.map((option) => {
      const optionId = option.id.toString();
      const breakdown = voteBreakdown?.[optionId];

      if (!breakdown) {
        return {
          ...option,
          votes: option.votes || 0,
        };
      }

      let filteredVotes: number;
      switch (voteFilter) {
        case "verified":
          filteredVotes = breakdown.verified;
          break;
        case "unverified":
          filteredVotes = breakdown.unverified;
          break;
        case "anonymous":
          filteredVotes = breakdown.anonymous;
          break;
        case "anonymized":
          filteredVotes = breakdown.anonymized;
          break;
        case "all":
        default:
          filteredVotes = breakdown.total;
          break;
      }

      return {
        ...option,
        votes: filteredVotes,
      };
    }) || [];

  // Calculate filtered total votes
  const filteredTotalVotes = filteredOptions.reduce(
    (sum, option) => sum + (option.votes || 0),
    0
  );

  const handleFilterChange = (newFilter: VoteFilterType) => {
    setVoteFilter(newFilter);
  };

  return {
    voteFilter,
    handleFilterChange,
    voteStats,
    filteredOptions,
    filteredTotalVotes,
    isLoadingBreakdown,
    error,
  };
};
